import { userError } from "@/lib/userError";
import React, { useRef, useState } from "react";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { getTasks, getReminders, updateTask, updateReminder } from "@/lib/apiClient";
import { localDateTimeInput } from "@/lib/editDates";
import { taskDateInput, taskTimeInput } from "@/lib/taskDates";
import { RECURRENCE } from "@/lib/constants";
import { DateShortcuts } from "@/components/DateShortcuts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function EditItemDialog({ item, kind, onUpdated, disabled = false }) {
  const reminder = kind === "reminder";
  const noun = reminder ? "muistutusta" : "tehtävää";
  const prefix = `${kind}-edit-${item.id}`;
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [when, setWhen] = useState("");
  const [clock, setClock] = useState("");
  const [choice, setChoice] = useState("");
  const [saving, setSaving] = useState(false);
  const retainedDraft = useRef(null);
  const busy = useRef(false);
  const original = useRef(null);
  const update = reminder ? updateReminder : updateTask;

  const changeOpen = (next) => {
    if (busy.current) return;
    if (next) {
      original.current = reminder
        ? { title: item.title, remind_at: item.remind_at, recurrence: item.recurrence || "none" }
        : { title: item.title, due: item.due || null, priority: item.priority === "normal" ? "medium" : item.priority || "medium" };
      const draft = retainedDraft.current?.id === item.id ? retainedDraft.current : null;
      setTitle(draft?.title ?? item.title);
      setWhen(draft?.when ?? (reminder ? localDateTimeInput(item.remind_at) : taskDateInput(item.due)));
      setClock(draft?.clock ?? taskTimeInput(item.due));
      setChoice(draft?.choice ?? (reminder ? original.current.recurrence : original.current.priority));
    }
    if (!next) retainedDraft.current = { id: item.id, title, when, clock, choice };
    setOpen(next);
  };

  const save = async (event) => {
    event.preventDefault();
    if (busy.current) return;
    if (!title.trim() || (reminder && (!when || Number.isNaN(new Date(when).getTime())))) {
      toast.error(reminder ? "Täytä aihe ja kelvollinen aika." : "Anna tehtävälle nimi.");
      return;
    }
    const before = { ...original.current };
    // Preserve the exact original timestamp if only the title or priority changed.
    const payload = reminder
      ? { title: title.trim(), remind_at: when === localDateTimeInput(before.remind_at) ? before.remind_at : new Date(when).toISOString(), recurrence: choice }
      : { title: title.trim(), due: when === taskDateInput(before.due) && clock === taskTimeInput(before.due) ? before.due : when ? `${when}${clock?`T${clock}`:""}` : null, priority: choice };
    if (JSON.stringify(payload) === JSON.stringify(before)) { setOpen(false); return; }
    busy.current = true;
    setSaving(true);
    try {
      const saved = await update(item.id, { ...payload });
      // The server resolves manual Finnish wall clocks, including DST offsets.
      if (!reminder && saved && Object.prototype.hasOwnProperty.call(saved, "due")) payload.due = saved.due;
      retainedDraft.current = null;
      setOpen(false);
      let undoBusy = false;
      let undone = false;
      toast.success(reminder ? "Muistutuksen muutokset tallennettu." : "Tehtävän muutokset tallennettu.", {
        duration: 12000,
        action: { label: "Kumoa", onClick: async () => {
          if (undoBusy || undone) return;
          undoBusy = true;
          try {
            // Only undo if these fields still match this save, so a later edit is not overwritten.
            const current = await (reminder ? getReminders() : getTasks("all"));
            const latest = current.find((record) => record.id === item.id);
            const matches = latest && Object.entries(payload).every(([key, value]) =>
              key === "remind_at" ? new Date(latest[key]).getTime() === new Date(value).getTime()
                : (latest[key] ?? null) === (value ?? null));
            if (!matches) { toast.error("Tieto on muuttunut tallennuksen jälkeen. Avaa muokkaus uudelleen."); return; }
            await update(item.id, before);
            undone = true;
            await onUpdated();
            toast.success("Muutos kumottu.");
          } catch { toast.error("Kumoaminen ei onnistunut. Tarkista tiedot ja yritä uudelleen."); }
          finally { undoBusy = false; }
        } },
      });
      await onUpdated();
    } catch (error) {
      toast.error(userError(error, "Tallentaminen ei onnistunut. Muokkauksesi säilyivät, yritä uudelleen."));
    } finally { busy.current = false; setSaving(false); }
  };

  return <Dialog open={open} onOpenChange={changeOpen}>
    <DialogTrigger asChild><button type="button" disabled={disabled} data-testid={prefix}
      aria-label={`Muokkaa ${noun}: ${item.title}`}
      className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-full px-3 text-xs text-muted-foreground hover:bg-primary/10 hover:text-primary disabled:opacity-50">
      <Pencil className="h-4 w-4" /> Muokkaa
    </button></DialogTrigger>
    <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-sm overflow-y-auto rounded-2xl">
      <DialogHeader><DialogTitle>Muokkaa {noun}</DialogTitle>
        <DialogDescription>Muutokset tallennetaan vasta Tallenna-painikkeesta. Sulkeminen säilyttää luonnoksen tässä istunnossa. Peruuta hylkää sen.</DialogDescription>
      </DialogHeader>
      <form onSubmit={save} className="space-y-4">
        <fieldset disabled={saving} className="space-y-4">
          <div><Label htmlFor={`${prefix}-title`}>{reminder ? "Aihe" : "Tehtävän nimi"}</Label>
            <Input id={`${prefix}-title`} value={title} onChange={(event) => setTitle(event.target.value)} maxLength={500} required className="mt-1 rounded-xl" /></div>
          <div><Label htmlFor={`${prefix}-when`}>{reminder ? "Ajankohta" : "Eräpäivä"}</Label>
            <Input id={`${prefix}-when`} type={reminder ? "datetime-local" : "date"} value={when} onChange={(event) => setWhen(event.target.value)} required={reminder} className="mt-1 rounded-xl" />
            <DateShortcuts value={when} onChange={setWhen} withTime={reminder} disabled={saving} />
            {reminder && <p className="mt-2 text-xs text-muted-foreground">Aika näytetään laitteesi paikallisessa ajassa. Pikavalinta säilyttää kellonajan.</p>}
          </div>
          {!reminder && <div><Label htmlFor={`${prefix}-clock`}>Kellonaika (valinnainen)</Label>
            <Input id={`${prefix}-clock`} type="time" value={clock} disabled={!when} onChange={event=>setClock(event.target.value)} className="mt-1 rounded-xl" />
            <p className="mt-2 text-xs text-muted-foreground">Määräaika Suomen ajassa. Tyhjä kellonaika tarkoittaa koko päivän tehtävää.</p>
          </div>}
          <div><Label htmlFor={`${prefix}-choice`}>{reminder ? "Toistuvuus" : "Tärkeys"}</Label>
            <Select value={choice} onValueChange={setChoice} disabled={saving}>
              <SelectTrigger id={`${prefix}-choice`} className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(reminder ? RECURRENCE : { high: "Korkea", medium: "Keskitaso", low: "Matala" }).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}</SelectContent>
            </Select></div>
        </fieldset>
        <DialogFooter className="gap-2">
          <button type="button" disabled={saving} onClick={() => { retainedDraft.current = null; setOpen(false); }} className="min-h-11 rounded-full border border-border px-4 text-sm">Peruuta</button>
          <button type="submit" disabled={saving || !title.trim() || (reminder && !when)} className="glow-action min-h-11 rounded-full px-4 text-sm font-semibold disabled:opacity-50">{saving ? "Tallennetaan…" : "Tallenna muutokset"}</button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>;
}
